import { auth } from '~/config/auth';
import { ValidationError } from '~/errors';
import { envConfig } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type { TutorialDto, TrackTutorialClickDto, SearchTutorialsParams } from '~/types/tutorial';

export class TutorialRepository extends BaseRestRepository {
  private readonly baseUrl: string;

  constructor() {
    super();
    this.baseUrl = envConfig.service.education;
  }

  /**
   * Search for tutorials using semantic search with optional category filtering
   * Returns a maximum of 3 results
   * Note: Uses direct fetch to avoid triggering auth redirect on 401 from external service
   */
  public async searchTutorials(params: SearchTutorialsParams): Promise<TutorialDto[]> {
    if (!params.query || params.query.trim() === '') {
      throw new ValidationError('Search query must be provided');
    }

    const queryParams: Record<string, string | string[]> = {
      query: params.query,
    };

    if (params.categories && params.categories.length > 0) {
      queryParams.categories = params.categories;
    }

    try {
      const authHeaders = await auth.getAuthHeader();
      const url = urlWithQuery(`${this.baseUrl}/tutorials/search`, queryParams);
      const response = await fetch(url, {
        headers: {
          'content-type': 'application/json',
          ...authHeaders,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('Tutorial search received 401 - education service may be unavailable or misconfigured');
        }
        return [];
      }

      return this.getJsonResponse(response);
    } catch (error) {
      console.warn('Tutorial search failed:', error);
      return [];
    }
  }

  /**
   * Track when a user clicks on a tutorial from search results
   * Used for analytics and improving search relevance
   */
  public async trackTutorialClick(data: TrackTutorialClickDto): Promise<void> {
    if (!data.searchQuery) {
      throw new ValidationError('Search query must be provided');
    }
    if (!data.tutorialId) {
      throw new ValidationError('Tutorial ID must be provided');
    }

    const url = `${this.baseUrl}/tutorials/click-tracking`;
    await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const tutorialRepository = new TutorialRepository();
